import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["start", "end", "modelo", "user", "cards", "charts", "fasesTitle"]

  connect() {
    this.load()
  }

  reload() {
    this.load()
  }

  // ------------------------------------
  // Helpers para formatação de tempo
  // ------------------------------------
  formatDuration(seconds) {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}min`
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`
    return `${(seconds / 86400).toFixed(1)}d`
  }

  formatTooltip(seconds) {
    const d = Math.floor(seconds / 86400)
    seconds %= 86400
    const h = Math.floor(seconds / 3600)
    seconds %= 3600
    const m = Math.floor(seconds / 60)
    const s = seconds % 60

    let parts = []
    if (d > 0) parts.push(`${d}d`)
    if (h > 0) parts.push(`${h}h`)
    if (m > 0) parts.push(`${m}m`)
    if (s > 0) parts.push(`${s}s`)

    return parts.join(" ")
  }

  async load() {
    const params = new URLSearchParams({
      start_date: this.startTarget.value,
      end_date: this.endTarget.value,
      modelo_id: this.modeloTarget.value,
      user_id: this.userTarget.value
    })

    const response = await fetch(`/dashboards/fluxo_stats?${params}`)
    const data = await response.json()

    this.fadeIn(this.chartsTarget)

    this.renderCards(data.fluxos)

    if (!this.modeloTarget.value) {
      this.renderStackedFluxosChart(data.stacked)
    } else {
      this.renderFasesChart(data.fases)
    }

    this.renderUsuariosChart(data.usuarios)
    this.renderFluxoVolumeChart(data.fluxo_volume)
  }

  // ------------------------------------
  // Cards
  // ------------------------------------
  renderCards(fluxos) {
    if (!this.cardsTarget) return

    const { avg_seconds, min_seconds, max_seconds, total_fluxos, fluxos_por_dia, avg_change_pct } = fluxos
    const format = (s) => this.formatTooltip(s)

    this.cardsTarget.innerHTML = `
      <!-- THROUGHPUT -->
      <div class="col-md-3 mt-3">
        <div class="card shadow-lg border-0 text-white" style="background: linear-gradient(135deg, #7b2ff7, #4c1d95);">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h6 class="fw-bold text-white-50 mb-1">Volume médio diário</h6>
                <h3 class="fw-bold">${fluxos_por_dia ?? '--'}</h3>
                <small class="text-white">Volume total</small>
                <strong class="text-white">${total_fluxos ?? '--'}</strong>
              </div>
              <div class="display-6 opacity-75 ms-2">
                <i class="bi bi-bar-chart-line"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MÉDIA -->
      <div class="col-md-3 mb-3">
        <div class="card shadow-lg border-0 text-white" style="background: linear-gradient(135deg, #4e73df, #224abe);">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h6 class="fw-bold text-white-50 mb-1">Média do fluxo</h6>
                <h2 class="fw-bold">${format(avg_seconds)}</h2>
                <small class="${avg_change_pct >= 0 ? 'text-danger' : 'text-success'} fw-bold">
                  ${avg_change_pct >= 0 ? '▲' : '▼'} ${Math.abs(avg_change_pct)}% vs período anterior
                </small>
              </div>
              <div class="display-6 opacity-75">
                <i class="bi bi-stopwatch"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MÍNIMO -->
      <div class="col-md-3 mb-3">
        <div class="card shadow-lg border-0 text-white" style="background: linear-gradient(135deg, #1cc88a, #13855c);">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h6 class="fw-bold text-white-50 mb-1">Mínimo do fluxo</h6>
                <h2 class="fw-bold">${format(min_seconds)}</h2>
                <small class="text-white-75">Melhor tempo registrado</small>
              </div>
              <div class="display-6 opacity-75">
                <i class="bi bi-lightning-charge"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MÁXIMO -->
      <div class="col-md-3 mb-3">
        <div class="card shadow-lg border-0 text-white" style="background: linear-gradient(135deg, #e74a3b, #be2617);">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <div>
                <h6 class="fw-bold text-white-50 mb-1">Máximo do fluxo</h6>
                <h2 class="fw-bold">${format(max_seconds)}</h2>
                <small class="text-white-75">Pior tempo registrado</small>
              </div>
              <div class="display-6 opacity-75">
                <i class="bi bi-activity"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  // ------------------------------------
  // Gráfico Fases (com tempo dinâmico)
  // ------------------------------------
  renderFasesChart(rows) {
    const el = this.chartsTarget?.querySelector("#chart-fases")
    if (!el) return
    if (this.fasesChart) this.fasesChart.destroy()

    const format = this.formatDuration.bind(this)
    const tooltip = this.formatTooltip.bind(this)

    this.fasesChart = new ApexCharts(el, {
      chart: { type: "bar", height: 280 },

      series: [{
        name: "Média",
        data: rows.map(r => ({
          x: r.fase_nome || `Fase ${r.fase_id}`,
          y: r.avg_seconds,
          label: format(r.avg_seconds)
        }))
      }],

      dataLabels: {
        enabled: true,
        formatter: (val) => format(val)
      },

      yaxis: {
        labels: { formatter: format },
        title: { text: "Tempo médio" }
      },

      tooltip: {
        y: { formatter: tooltip }
      },

      plotOptions: {
        bar: { borderRadius: 4 }
      }
    })

    this.fasesChart.render()
    this.fasesTitleTarget.innerText = "Tempo por fase"
  }

  // ------------------------------------
  // Gráfico Usuários
  // ------------------------------------
  renderUsuariosChart(rows) {
    const el = this.chartsTarget?.querySelector("#chart-usuarios")
    if (!el) return
    if (this.usuariosChart) this.usuariosChart.destroy()

    const format = this.formatDuration.bind(this)
    const tooltip = this.formatTooltip.bind(this)

    this.usuariosChart = new ApexCharts(el, {
      chart: { type: "bar", height: 280 },

      series: [{
        name: "Média",
        data: rows.map(r => ({
          x: r.user_nome || `User ${r.user_id}`,
          y: r.avg_seconds,
          label: format(r.avg_seconds)
        }))
      }],

      dataLabels: {
        enabled: true,
        formatter: val => format(val)
      },

      yaxis: {
        labels: { formatter: format }
      },

      tooltip: {
        y: { formatter: tooltip }
      },

      plotOptions: { bar: { borderRadius: 4 } }
    })

    this.usuariosChart.render()
    
  }

  // ------------------------------------
  // Gráfico Volume
  // ------------------------------------
  renderFluxoVolumeChart(rows) {
    const el = document.querySelector("#chart-volume")
    if (this.volumeChart) this.volumeChart.destroy()

    let options

    if (!this.modeloTarget.value) {
      options = {
        chart: { type: "bar", height: 280 },
        series: [{ name: "Fluxos", data: rows.map(r => r.quantity) }],
        xaxis: { categories: rows.map(r => r.modelo_nome) },
        plotOptions: { bar: { borderRadius: 4 } }
      }
    } else {
      options = {
        chart: { type: "line", height: 280 },
        series: [{ name: "Fluxos por dia", data: rows.map(r => r.quantity) }],
        xaxis: { categories: rows.map(r => r.date) },
      }
    }

    this.volumeChart = new ApexCharts(el, options)
    this.volumeChart.render()
  }

  // ------------------------------------
  // Gráfico Stacked
  // ------------------------------------
  renderStackedFluxosChart(raw) {
    const el = this.chartsTarget?.querySelector("#chart-fases")
    if (!el) return
    if (this.fasesChart) this.fasesChart.destroy()

    const format = this.formatDuration.bind(this)
    const tooltip = this.formatTooltip.bind(this)

    const labels = raw.map(item => item.label)

    const grouped = {}
    raw.forEach(item => {
      grouped[item.fluxo_id] = item.fases.map(f => ({
        fase: f.fase_nome,
        ordem: f.ordem,
        total_seconds: f.seconds
      }))
    })

    const fluxoIds = Object.keys(grouped).sort()

    const fasesUnicas = []
    fluxoIds.forEach(id => {
      grouped[id].forEach(f => {
        if (!fasesUnicas.find(x => x.ordem === f.ordem)) {
          fasesUnicas.push({ ordem: f.ordem, fase: f.fase })
        }
      })
    })
    fasesUnicas.sort((a, b) => a.ordem - b.ordem)

    const series = fasesUnicas.map(fase => ({
      name: fase.fase,
      data: fluxoIds.map(fid => {
        const row = grouped[fid].find(r => r.ordem === fase.ordem)
        return row ? row.total_seconds : 0
      })
    }))

    this.fasesChart = new ApexCharts(el, {
      chart: { type: "bar", height: 280, stacked: true },

      series,

      dataLabels: {
        enabled: false
      },

      xaxis: { categories: labels },

      yaxis: {
        labels: { formatter: format },
        title: { text: "Tempo total por fluxo" }
      },

      tooltip: {
        y: { formatter: tooltip }
      },

      legend: { show: false },
      plotOptions: { bar: { borderRadius: 4 } }
    })

    this.fasesChart.render()
    this.fasesTitleTarget.innerText = "Tempo total por Fluxo (Fases Empilhadas)"
  }

  fadeIn(el) {
    el.style.opacity = 0
    el.style.transition = "opacity .3s ease"
    requestAnimationFrame(() => (el.style.opacity = 1))
  }
}
